import React, { useState, useEffect } from 'react';
import WidgetWrapper from './WidgetWrapper';
import { customers, entries, invoices, reports } from '../../services/api';

const StatsWidget = ({ darkMode = false, onError }) => {
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < 800;
  const isTablet = windowWidth >= 768 && windowWidth < 1200;
  
  const [summary, setSummary] = useState({
    totalCustomers: 0,
    totalEntries: 0,
    totalRevenue: 0,
    pendingInvoices: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        
        const [customersRes, invoicesRes, reportsRes] = await Promise.all([
          customers.list({ limit: 1 }),
          invoices.list({ status: 'unbilled' }),
          reports.summary({ startDate: year + "-01-01", endDate: year + "-12-31" })
        ]);

        setSummary({
          totalCustomers: customersRes.data?.length || customersRes.length || 0,
          totalEntries: reportsRes.data?.totalEntries || 0,
          totalRevenue: (reportsRes.data?.unbilled?.amount || 0) + 
                       (reportsRes.data?.invoiced?.amount || 0) + 
                       (reportsRes.data?.paid?.amount || 0),
          pendingInvoices: invoicesRes.data?.total || 0
        });
      } catch (error) {
        console.error('StatsWidget error:', error);
        onError?.('StatsWidget', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onError]);

  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const textColor = darkMode ? '#f3f4f6' : '#111827';

  const stats = [
    { label: 'Total Customers', value: summary.totalCustomers },
    { label: 'Time Entries', value: summary.totalEntries },
    { label: 'Total Revenue', value: `$${summary.totalRevenue.toLocaleString()}`, class: 'success' },
    { label: 'Pending Invoices', value: summary.pendingInvoices, class: 'warning' }
  ];

  return (
    <WidgetWrapper title="📊 Overview" loading={loading} darkMode={darkMode}>
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: isMobile ? 8 : 16 }}>
        {stats.map((stat, i) => (
          <div key={i} className="stat-card" style={{ 
            background: darkMode ? '#1f2937' : '#fff', 
            padding: isMobile ? 8 : 16, 
            borderRadius: 8 
          }}>
            <div className="stat-label" style={{ color: subTextColor, fontSize: 12 }}>{stat.label}</div>
            <div className={`stat-value ${stat.class || ''}`} style={{ 
              color: stat.class === 'success' ? '#22c55e' : stat.class === 'warning' ? '#f59e0b' : textColor, 
              fontSize: 20, 
              fontWeight: 'bold' 
            }}>{stat.value}</div>
          </div>
        ))}
      </div>
    </WidgetWrapper>
  );
};

// Widget metadata
StatsWidget.widgetId = 'stats';
StatsWidget.title = '📊 Overview';
StatsWidget.defaultSize = { cols: 12 };
StatsWidget.category = 'business';

export default StatsWidget;